import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SecurityGuide = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const securitySteps = [
    {
      id: 'credentials',
      title: 'Credenciales AWS',
      icon: 'Key',
      content: [
        'Crea un usuario IAM específico para esta aplicación',
        'Asigna únicamente permisos de RDS necesarios',
        'Nunca compartas tu Secret Access Key',
        'Rota las credenciales periódicamente'
      ]
    },
    {
      id: 'network',
      title: 'Configuración de Red',
      icon: 'Shield',
      content: [
        'Configura Security Groups para permitir acceso solo desde IPs específicas',
        'Habilita SSL/TLS para conexiones seguras',
        'Considera usar VPC Endpoints para mayor seguridad',
        'Documenta todas las reglas de firewall'
      ]
    },
    {
      id: 'database',
      title: 'Seguridad de Base de Datos',
      icon: 'Database',
      content: [
        'Usa contraseñas fuertes para usuarios de BD',
        'Habilita encriptación en reposo',
        'Configura backups automáticos',
        'Monitorea logs de acceso regularmente'
      ]
    }
  ];

  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  const handleSupportContact = () => {
    // Open support contact method - can be email, chat, or modal
    const supportEmail = 'soporte@mercadolibre-bulk-publisher.com';
    const subject = 'Ayuda con Conexión AWS Database';
    const body = encodeURIComponent(`Hola,

Necesito ayuda con la configuración de la conexión AWS Database.

Detalles de mi consulta:
- Página: Conexión AWS Database
- Problema: [Describe tu problema aquí]

Gracias por su ayuda.`);
    
    window.open(`mailto:${supportEmail}?subject=${subject}&body=${body}`, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* AWS Connection Setup Guide */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <div className="flex items-center space-x-3 mb-3">
          <Icon name="Info" size={20} className="text-primary flex-shrink-0" />
          <h3 className="text-lg font-semibold text-text-primary leading-tight">
            Crear Conexión AWS
          </h3>
        </div>
        <p className="text-sm text-text-secondary mb-3 leading-relaxed">
          Antes de configurar la conexión, necesitas crear los recursos AWS:
        </p>
        <div className="space-y-2">
          <a
            href="https://console.aws.amazon.com/rds/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between p-2 bg-white border border-primary/30 rounded-md hover:bg-primary/10 transition-colors group"
          >
            <div className="flex items-center space-x-2 min-w-0">
              <Icon name="Database" size={14} className="text-primary flex-shrink-0" />
              <span className="text-sm font-medium text-text-primary truncate">
                Crear RDS Database
              </span>
            </div>
            <Icon name="ExternalLink" size={12} className="text-primary flex-shrink-0 group-hover:scale-110 transition-transform" />
          </a>
          
          <a
            href="https://console.aws.amazon.com/iam/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between p-2 bg-white border border-primary/30 rounded-md hover:bg-primary/10 transition-colors group"
          >
            <div className="flex items-center space-x-2 min-w-0">
              <Icon name="Key" size={14} className="text-primary flex-shrink-0" />
              <span className="text-sm font-medium text-text-primary truncate">
                Crear Usuario IAM
              </span>
            </div>
            <Icon name="ExternalLink" size={12} className="text-primary flex-shrink-0 group-hover:scale-110 transition-transform" />
          </a>
        </div>
      </div>

      {/* Security Guide */}
      <div className="bg-surface border border-border rounded-lg p-4 shadow-soft">
        <div className="flex items-center space-x-3 mb-4">
          <Icon name="Shield" size={20} className="text-primary flex-shrink-0" />
          <h3 className="text-lg font-semibold text-text-primary leading-tight">
            Guía de Seguridad
          </h3>
        </div>

        <div className="space-y-3">
          {securitySteps?.map((step) => (
            <div key={step?.id} className="border border-border rounded-lg overflow-hidden">
              <Button
                variant="ghost"
                onClick={() => toggleSection(step?.id)}
                className="w-full justify-between p-3 hover:bg-muted/50"
              >
                <div className="flex items-center space-x-3 min-w-0">
                  <Icon name={step?.icon} size={16} className="text-primary flex-shrink-0" />
                  <span className="font-medium text-text-primary text-sm leading-tight text-left truncate">
                    {step?.title}
                  </span>
                </div>
                <Icon 
                  name="ChevronDown" 
                  size={16} 
                  className={`transition-transform flex-shrink-0 ${
                    expandedSection === step?.id ? 'rotate-180' : ''
                  }`}
                />
              </Button>
              
              {expandedSection === step?.id && (
                <div className="px-3 pb-3 pt-0 border-t border-border/50">
                  <ul className="space-y-2 mt-2">
                    {step?.content?.map((item, index) => (
                      <li key={index} className="flex items-start space-x-2 text-sm">
                        <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                        <span className="text-text-secondary leading-relaxed break-words">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Quick Links */}
      <div className="bg-surface border border-border rounded-lg p-4 shadow-soft">
        <h3 className="text-lg font-semibold text-text-primary mb-4 leading-tight">
          Enlaces Útiles
        </h3>
        
        <div className="space-y-2">
          <a
            href="https://docs.aws.amazon.com/rds/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors group"
          >
            <div className="flex items-center space-x-3 min-w-0">
              <Icon name="ExternalLink" size={16} className="text-primary flex-shrink-0" />
              <span className="text-sm font-medium text-text-primary truncate">
                Documentación RDS
              </span>
            </div>
            <Icon name="ChevronRight" size={14} className="text-text-secondary flex-shrink-0 group-hover:translate-x-1 transition-transform" />
          </a>

          <a
            href="https://docs.aws.amazon.com/iam/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors group"
          >
            <div className="flex items-center space-x-3 min-w-0">
              <Icon name="ExternalLink" size={16} className="text-primary flex-shrink-0" />
              <span className="text-sm font-medium text-text-primary truncate">
                Guía IAM
              </span>
            </div>
            <Icon name="ChevronRight" size={14} className="text-text-secondary flex-shrink-0 group-hover:translate-x-1 transition-transform" />
          </a>

          <a
            href="https://docs.aws.amazon.com/vpc/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors group"
          >
            <div className="flex items-center space-x-3 min-w-0">
              <Icon name="ExternalLink" size={16} className="text-primary flex-shrink-0" />
              <span className="text-sm font-medium text-text-primary truncate">
                Configuración VPC
              </span>
            </div>
            <Icon name="ChevronRight" size={14} className="text-text-secondary flex-shrink-0 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>

      {/* Support Contact */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <div className="flex items-center space-x-3 mb-3">
          <Icon name="HelpCircle" size={20} className="text-primary flex-shrink-0" />
          <h3 className="text-lg font-semibold text-text-primary leading-tight">
            ¿Necesitas Ayuda?
          </h3>
        </div>
        <p className="text-sm text-text-secondary mb-4 leading-relaxed">
          Si tienes problemas configurando la conexión, nuestro equipo puede ayudarte.
        </p>
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full" 
          onClick={handleSupportContact}
        >
          <Icon name="MessageCircle" size={16} className="mr-2 flex-shrink-0" />
          Contactar Soporte
        </Button>
      </div>
    </div>
  );
};

export default SecurityGuide;